/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { fetchCrimeReferencesById } from "services/incidentService";
import { CrimeReference, Investigation, Roles } from "types";
import { errorHandler, hasRole } from "utils/helper";
import TabNav from "../../../components/TabNav";
import MainLayout from "../../../layouts/MainLayout";
import AssignedToTab from "../IncidentDetail/AssignedToTab";
import EvidenceFilesTab from "../IncidentDetail/EvidenceFilesTab";
import ExhibitsTab from "../IncidentDetail/ExhibitsTab";
import CrimeFinalInformation from "../IncidentDetail/fragment/CrimeFinalInformation";
import Forward from "../IncidentDetail/fragment/Forward";
import ScreeningMembers from "../IncidentDetail/fragment/ScreeningMembers";
import IncidentInformationTab from "../IncidentDetail/IncidentInformationTab";
import "../IncidentDetail/incidentRegister.css";
import InvestigationTab from "../IncidentDetail/InvestigationTab";
import ReporterTab from "../IncidentDetail/ReporterTab";
import ScreeningTab from "../IncidentDetail/ScreeningTab";
import SuspectTab from "../IncidentDetail/SuspectTab";
import VictimsTab from "../IncidentDetail/VictimsTab";
import WitnessesTab from "../IncidentDetail/WitnessesTab";
import IncidentRegister from "../IncidentRegister/IncidentRegister";

// Reusable Card Component
const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}> = ({ children, className = "", onClick }) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${
      onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""
    } ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

const tab = [
  "Incident Information",
  "Reporter",
  "Evidence Files",
  "Victims",
  "Suspects",
  "Witness",
  "Exhibits",
  "Investigation",
];

const ClerkIncident: React.FC = () => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { state: locationState } = useLocation();
  const [activeTab, setActiveTab] = useState(tab[0]);
  const params = useParams();
  const [refetch, setRefetch] = useState(true);
  const [loading, setLoading] = useState(true);
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [incident, setIncident] = useState<CrimeReference>();
  const [changeTab, setChangeTab] = useState("");

  const canManageIncidentDetail = hasRole(loginData?.roles, [
    Roles.prosecutor,
    Roles.member,
    Roles.clerk,
    Roles.economicClerk,
    Roles.corruptionClerk,
    Roles.economicDirector,
    Roles.corruptionDirector,
    Roles.screeningTeamCoordinator,
  ]);

  const fetchData = (id: string | undefined) => {
    if (!id) return;

    setLoading(true);
    fetchCrimeReferencesById(id)
      .then((data) => {
        setIncident(data);
        setInvestigations(data?.investigations || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      });
  };

  useEffect(() => {
    fetchData(params?.incidentId);
  }, [params?.incidentId, refetch]);

  return (
    <MainLayout>
      {/* Header Section */}
      <Card className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">⚖</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Incident Details
              </h1>
              <p className="text-gray-600 mt-1">
                Manage and review incident information and related processes.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {/* <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="font-semibold text-red-600">Crime Ref#:</span>
              <span className="text-gray-900 font-medium">
                {incident?.CR_number || "-"}
              </span>
            </div> */}
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="font-semibold text-red-600">Incident Reg#:</span>
              <span className="text-gray-900 font-medium">
                {incident?.Incident_Registration_number || "-"}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <TabNav active={activeTab} tabs={tab} onSelect={setActiveTab} />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === "Incident Information" ? (
              incident?.status === "forwarded" ? (
                <IncidentInformationTab
                  incident={incident}
                  setRefetch={setRefetch}
                  canManageIncidentDetail={canManageIncidentDetail}
                />
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <IncidentRegister incident={incident} />
                </div>
              )
            ) : activeTab === "Reporter" && incident ? (
              <ReporterTab
                investigation={investigations[0]}
                incident={incident}
                canManageIncidentDetail={canManageIncidentDetail}
              />
            ) : activeTab === "Evidence Files" && incident ? (
              <EvidenceFilesTab
                investigation={investigations[0]}
                incident={incident}
                canManageIncidentDetail={canManageIncidentDetail}
              />
            ) : activeTab === "Victims" && incident ? (
              <VictimsTab
                investigation={investigations[0]}
                incident={incident}
                canManageIncidentDetail={canManageIncidentDetail}
              />
            ) : activeTab === "Suspects" && incident ? (
              <SuspectTab
                investigation={investigations[0]}
                incident={incident}
                canManageIncidentDetail={canManageIncidentDetail}
              />
            ) : activeTab === "Witness" && incident ? (
              <WitnessesTab
                investigation={investigations[0]}
                incident={incident}
                canManageIncidentDetail={canManageIncidentDetail}
              />
            ) : activeTab === "Exhibits" ? (
              <ExhibitsTab
                investigation={investigations[0]}
                incident={incident}
                canManageIncidentDetail={canManageIncidentDetail}
              />
            ) : activeTab === "Assigned to" ? (
              <AssignedToTab />
            ) : activeTab === "Investigation" ? (
              <InvestigationTab
                incident={incident}
                file={locationState?.file}
                investigations={investigations}
                setRefetch={setRefetch}
                setChangeTab={setChangeTab}
              />
            ) : activeTab === "Screening" ? (
              <ScreeningTab incident={incident} setRefetch={setRefetch} />
            ) : null}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {activeTab === "Screening" && incident && (
              <>
                {hasRole(loginData?.roles, [
                  Roles.screeningTeamCoordinator,
                  Roles.coordinator,
                  Roles.prosecutionDirector,
                ]) &&
                  incident?.screening_members &&
                  incident?.screening_members?.length > 0 && (
                    <Card>
                      <CrimeFinalInformation
                        incident={incident}
                        setRefetch={setRefetch}
                        setChangeTab={setChangeTab}
                      />
                    </Card>
                  )}
                {hasRole(loginData?.roles, [Roles.coordinator]) && (
                  <Card>
                    <ScreeningMembers
                      incident={incident}
                      setRefetch={setRefetch}
                      setChangeTab={setChangeTab}
                    />
                  </Card>
                )}
              </>
            )}

            {hasRole(loginData?.roles, [
              Roles.member,
              Roles.prosecutor,
              Roles?.clerk,
            ]) &&
              activeTab === "Investigation" &&
              investigations.length > 0 &&
              incident && (
                <>
                  {investigations[0]?.end_date && (
                    <Card>
                      <Forward
                        incident={incident}
                        setRefetch={setRefetch}
                        setChangeTab={setChangeTab}
                        investigation={investigations[0]}
                      />
                    </Card>
                  )}
                </>
              )}
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ClerkIncident;
